import type { DestinoRef, ResultadoDaChamada } from './tipos';

/**
 * Aplicar no cliente (#606, D4): o destino recebe as suas chamadas, e o resultado diz, por nome,
 * o que foi aplicado, o que já existia e o que falhou — para a pessoa repetir só o que falhou.
 */
export const CHAMADAS_SIMULTANEAS = 3;

export interface ItemParaAplicar<V> {
  itemId: number;
  nome: string;
  valores: V;
}

export interface PedidoDoDestino<V> {
  destino: DestinoRef;
  itens: ItemParaAplicar<V>[];
}

export interface FalhaDoItem<V> extends ItemParaAplicar<V> {
  motivo: string;
}

export interface ResultadoDoDestino<V> {
  destino: DestinoRef;
  aplicados: ItemParaAplicar<V>[];
  jaExistiam: ItemParaAplicar<V>[];
  falharam: FalhaDoItem<V>[];
}

interface OpcoesDeAplicacao<V> {
  aplicar: (destino: DestinoRef, itemIds: number[], valores: V) => Promise<ResultadoDaChamada>;
  chaveDoGrupo: (valores: V) => string;
}

/** Roda as tarefas com no máximo `limite` ao mesmo tempo; o resultado sai na ordem das tarefas. */
export async function executarComLimite<T>(tarefas: (() => Promise<T>)[], limite: number): Promise<T[]> {
  const resultados: T[] = new Array(tarefas.length);
  let proxima = 0;
  const trabalhador = async () => {
    while (proxima < tarefas.length) {
      const indice = proxima;
      proxima += 1;
      resultados[indice] = await tarefas[indice]();
    }
  };
  await Promise.all(Array.from({ length: Math.min(limite, tarefas.length) }, trabalhador));
  return resultados;
}

function motivoDoErro(erro: unknown): string {
  return erro instanceof Error && erro.message ? erro.message : 'Não foi possível aplicar.';
}

/**
 * Uma chamada por destino e por grupo de valores iguais — a API em lote grava um valor só por
 * chamada (padrão de `useAplicarExamesDireto`). Chamada que lança marca todos os itens dela como
 * falhos: é o que acontece com riscos, cujo lote relança qualquer erro que não seja duplicidade.
 */
export async function aplicarAosDestinos<V>(
  pedidos: PedidoDoDestino<V>[],
  { aplicar, chaveDoGrupo }: OpcoesDeAplicacao<V>
): Promise<ResultadoDoDestino<V>[]> {
  const resultados: ResultadoDoDestino<V>[] = pedidos.map(({ destino }) => ({
    destino,
    aplicados: [],
    jaExistiam: [],
    falharam: [],
  }));

  const tarefas: (() => Promise<void>)[] = [];
  const agendar = (indice: number, grupo: ItemParaAplicar<V>[], chamar: () => Promise<ResultadoDaChamada>) =>
    tarefas.push(async () => {
      const resultado = resultados[indice];
      const porId = new Map(grupo.map((item) => [item.itemId, item]));
      try {
        const resposta = await chamar();
        const doGrupo = (id: number | string) => porId.get(Number(id));
        resultado.aplicados.push(...resposta.aplicados.map(doGrupo).filter(existe));
        resultado.jaExistiam.push(...resposta.jaExistiam.map(doGrupo).filter(existe));
        for (const falha of resposta.falharam) {
          const item = doGrupo(falha.itemId);
          if (item) resultado.falharam.push({ ...item, motivo: falha.motivo });
        }
      } catch (erro: unknown) {
        const motivo = motivoDoErro(erro);
        resultado.falharam.push(...grupo.map((item) => ({ ...item, motivo })));
      }
    });

  pedidos.forEach(({ destino, itens }, indice) => {
    const grupos = new Map<string, ItemParaAplicar<V>[]>();
    for (const item of itens) {
      const chave = chaveDoGrupo(item.valores);
      grupos.set(chave, [...(grupos.get(chave) ?? []), item]);
    }
    for (const grupo of grupos.values()) {
      agendar(indice, grupo, () => aplicar(destino, grupo.map((item) => item.itemId), grupo[0].valores));
    }
  });

  await executarComLimite(tarefas, CHAMADAS_SIMULTANEAS);
  return resultados;
}

function existe<T>(valor: T | undefined): valor is T {
  return valor !== undefined;
}

/** O que reenviar em "Repetir os que falharam": só os destinos e itens que falharam. */
export function pedidosDosFalhos<V>(resultados: ResultadoDoDestino<V>[]): PedidoDoDestino<V>[] {
  return resultados
    .filter((resultado) => resultado.falharam.length > 0)
    .map(({ destino, falharam }) => ({
      destino,
      itens: falharam.map(({ itemId, nome, valores }) => ({ itemId, nome, valores })),
    }));
}

/** O resultado da repetição substitui as falhas anteriores do mesmo destino. */
export function juntarResultados<V>(
  anteriores: ResultadoDoDestino<V>[],
  repeticao: ResultadoDoDestino<V>[]
): ResultadoDoDestino<V>[] {
  return anteriores.map((anterior) => {
    const novo = repeticao.find((r) => Number(r.destino.id) === Number(anterior.destino.id));
    if (!novo) return anterior;
    return {
      destino: anterior.destino,
      aplicados: [...anterior.aplicados, ...novo.aplicados],
      jaExistiam: [...anterior.jaExistiam, ...novo.jaExistiam],
      falharam: novo.falharam,
    };
  });
}
